import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, TrendingUp, Wrench, Hammer } from 'lucide-react';
import { companyData, services, trackRecord, newsData } from '../mock/mockData';

const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section with Video */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover z-0"
        >
          <source src="/videos/hero_video.mp4" type="video/mp4" />
        </video>
        
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70 z-0" />

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="font-serif text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight">
            TerniEnergia Progetti
          </h1>
          <p className="font-serif text-3xl md:text-4xl text-white/90 mb-8 italic">
            A waterfall of power
          </p>
          <p className="text-xl md:text-2xl text-white/80 mb-12 max-w-3xl mx-auto leading-relaxed">
            Soluzioni EPC full-scope, O&M e Asset Management per impianti fotovoltaici utility-scale.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link
              to="/esperienza-internazionale"
              className="px-8 py-4 bg-white text-[#081221] rounded-lg font-semibold text-lg hover:bg-gray-100 transition-all transform hover:scale-105 flex items-center space-x-2"
            >
              <span>Esperienza Internazionale</span>
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/about"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border-2 border-white rounded-lg font-semibold text-lg hover:bg-white/20 transition-all flex items-center space-x-2"
            >
              <span>Chi Siamo</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1.5 h-3 bg-white/50 rounded-full mt-2 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Key Numbers Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {companyData.keyNumbers.map((item, index) => (
              <div
                key={index}
                className="text-center p-8 bg-gradient-to-br from-[#f5f7fb] to-white rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1"
              >
                <div className="text-5xl font-bold font-serif text-[#0d63ce] mb-4">
                  {item.value}
                </div>
                <div className="text-gray-600 font-medium leading-relaxed">
                  {item.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Overview */}
      <section className="py-24 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6 leading-tight">
                Leader nel settore fotovoltaico utility-scale
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed mb-6">
                TerniEnergia Progetti è una società specializzata nella realizzazione e gestione di
                impianti fotovoltaici di grande taglia, con un track record internazionale di oltre
                614 MWp.
              </p>
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                Offriamo soluzioni integrate EPC, Operation & Maintenance e Asset Management,
                supportando clienti istituzionali e investitori nella valorizzazione del patrimonio
                fotovoltaico.
              </p>
              <Link
                to="/about"
                className="inline-flex items-center px-6 py-3 bg-[#081221] text-white rounded-lg font-semibold hover:bg-[#0d63ce] transition-colors space-x-2"
              >
                <span>Scopri di più</span>
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="relative">
              <img
                src="/impianto_reale_tep.jpg"
                alt="Impianto Fotovoltaico TerniEnergia Progetti"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -left-6 w-48 h-48 bg-gradient-to-br from-[#0d63ce] to-[#18a06b] rounded-2xl opacity-20 -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">I Nostri Servizi</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Soluzioni integrate per l'intero ciclo di vita degli impianti fotovoltaici
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon === 'Hammer' ? Hammer : service.icon === 'Wrench' ? Wrench : TrendingUp;
              return (
                <div
                  key={service.id}
                  className="group relative overflow-hidden rounded-2xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#081221]/95 via-[#081221]/60 to-transparent flex flex-col justify-end p-8">
                    <div className="mb-4">
                      <IconComponent className="text-[#18a06b]" size={36} />
                    </div>
                    <h3 className="font-serif text-2xl font-bold text-white mb-3">
                      {service.title}
                    </h3>
                    <p className="text-white/90 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/activities"
              className="inline-flex items-center px-6 py-3 bg-[#081221] text-white rounded-lg font-semibold hover:bg-[#0d63ce] transition-colors space-x-2"
            >
              <span>Scopri tutte le attività</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Global Presence Preview */}
      <section className="py-24 bg-[#081221] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold mb-6">Esperienza Internazionale</h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Oltre 614 MWp installati in 5 paesi
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-12">
            {trackRecord.countries.map((country, index) => (
              <div
                key={index}
                className="text-center p-6 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-colors"
              >
                <div className="text-3xl font-bold text-[#18a06b] mb-2">
                  {country.mwp} MWp
                </div>
                <div className="text-white/90 font-medium">{country.name}</div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <Link
              to="/esperienza-internazionale"
              className="inline-flex items-center px-6 py-3 bg-white text-[#081221] rounded-lg font-semibold hover:bg-gray-100 transition-colors space-x-2"
            >
              <span>Vedi Esperienza Internazionale</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Press Releases */}
      <section className="py-24 bg-[#f5f7fb]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="font-serif text-5xl font-bold text-[#081221] mb-4">
                Ultime News
              </h2>
              <p className="text-xl text-gray-600">
                Aggiornamenti aziendali e comunicati stampa
              </p>
            </div>
            <Link
              to="/news"
              className="hidden md:inline-flex items-center text-[#0d63ce] font-semibold hover:text-[#081221] transition-colors space-x-2"
            >
              <span>Vedi tutte le news</span>
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {newsData.map((news) => (
              <div
                key={news.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 overflow-hidden"
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img
                    src={news.image}
                    alt={news.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-3 mb-3">
                    <span className="px-3 py-1 bg-[#0d63ce]/10 text-[#0d63ce] text-xs font-semibold rounded-full">
                      {news.category}
                    </span>
                    <span className="text-gray-500 text-sm">
                      {new Date(news.date).toLocaleDateString('it-IT')}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl font-bold text-[#081221] mb-3 leading-tight">
                    {news.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{news.excerpt}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12 md:hidden">
            <Link
              to="/news"
              className="inline-flex items-center px-6 py-3 bg-[#081221] text-white rounded-lg font-semibold hover:bg-[#0d63ce] transition-colors space-x-2"
            >
              <span>Vedi tutte le news</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Clienti Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="font-serif text-5xl font-bold text-[#081221] mb-6">
              I Nostri Clienti
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Partner di eccellenza che hanno scelto TerniEnergia Progetti per i loro progetti utility-scale
            </p>
          </div>

          {/* Logos Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 items-center">
            {[
              { num: 115, ext: 'png', name: 'Circet' },
              { num: 116, ext: 'png', name: 'Acea' },
              { num: 117, ext: 'png', name: 'Enfinity' },
              { num: 118, ext: 'png', name: 'X-Elio' },
              { num: 119, ext: 'png', name: 'Verbund' },
              { num: 130, ext: 'png', name: 'ENI' },
              { num: 133, ext: 'png', name: 'Altenia' },
              { num: 132, ext: 'png', name: 'Ortus' },
              { num: 131, ext: 'png', name: 'Fiamma 2000' },
              { num: 128, ext: 'png', name: 'EDF' },
              { num: 127, ext: 'png', name: 'Aeroporti di Roma' },
              { num: 120, ext: 'jpeg', name: 'Terna' },
              { num: 121, ext: 'jpeg', name: 'Buzzi Unicem' },
              { num: 122, ext: 'png', name: 'Ferrero' },
              { num: 123, ext: 'png', name: 'Alchimia' },
              { num: 124, ext: 'png', name: 'CAF' },
              { num: 125, ext: 'png', name: 'Circet' },
              { num: 126, ext: 'png', name: 'Bester' }
            ].map((logo) => (
              <div
                key={logo.num}
                className="flex items-center justify-center p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-all hover:scale-105"
              >
                <img
                  src={`/logos/image${logo.num}.${logo.ext}`}
                  alt={logo.name}
                  className="max-h-16 w-auto object-contain transition-all"
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
